import React from "react";
import { 
  Box, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";

const useStyles = makeStyles(() => ({
  printContainer: {
    padding: "5mm 3mm",
    backgroundColor: "#fff",
    color: "#000",
    fontFamily: "'Roboto', 'Arial', sans-serif",
    fontSize: "7pt",
    lineHeight: "1.2",
    "& *": {
      boxSizing: "border-box",
    },
  },
  header: {
    textAlign: "center",
    marginBottom: "3mm",
    paddingBottom: "2mm",
    borderBottom: "2px solid #000",
  },
  title: {
    fontSize: "12pt",
    fontWeight: "bold",
    marginBottom: "1mm",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  subtitle: {
    fontSize: "7pt",
    color: "#666",
    marginTop: "1mm",
  },
  clientName: {
    fontSize: "10pt",
    fontWeight: "bold",
    marginTop: "1mm",
    color: "#000",
  },
  tableContainer: {
    marginTop: "2mm",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    "& th": {
      backgroundColor: "#333 !important",
      color: "#fff !important",
      padding: "1mm 1.5mm",
      fontSize: "6.5pt",
      fontWeight: "bold",
      textAlign: "left",
      border: "1px solid #000",
      whiteSpace: "nowrap",
      WebkitPrintColorAdjust: "exact",
      printColorAdjust: "exact",
    },
    "& td": {
      padding: "1mm 1.5mm",
      fontSize: "6.5pt",
      border: "1px solid #666",
      color: "#000",
      verticalAlign: "top",
      maxWidth: "40mm",
      wordWrap: "break-word",
      overflow: "hidden",
    },
  },
  emptyMessage: {
    fontSize: "7pt",
    color: "#999",
    fontStyle: "italic",
    textAlign: "center",
    padding: "3mm",
  },
  footer: {
    marginTop: "3mm",
    paddingTop: "2mm",
    borderTop: "1px solid #999",
    textAlign: "center",
    fontSize: "6.5pt",
    color: "#666",
  },
}));

const HistoricoPrintTemplate = React.forwardRef(({ cliente, historico }, ref) => {
  const classes = useStyles();

  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    try {
      return format(parseISO(dateString), "dd/MM/yyyy HH:mm", {
        locale: ptBR,
      });
    } catch (error) {
      return dateString;
    }
  };

  const formatDateOnly = (dateString) => {
    if (!dateString) return "";
    try {
      return format(parseISO(dateString), "dd/MM/yyyy", {
        locale: ptBR,
      });
    } catch (error) {
      return dateString;
    }
  };

  const getMotivoText = (motivo) => {
    if (!motivo) return "";
    return motivo.replace(/_/g, " ");
  };

  const truncateText = (text, maxLength = 80) => {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  return (
    <div ref={ref} className={classes.printContainer}>
      {/* Cabeçalho */}
      <Box className={classes.header}>
        <Typography className={classes.title}>
          Histórico de Relacionamento
        </Typography>
        <Typography className={classes.clientName}>
          {cliente?.NOMECLI || "Cliente"}
        </Typography>
        <Typography className={classes.subtitle}>
          Período: 15/11/2025 até 15/12/2025 | Impresso em: {format(new Date(), "dd/MM/yyyy HH:mm", { locale: ptBR })}
        </Typography>
      </Box>

      {/* Tabela de Históricos */}
      {!historico || historico.length === 0 ? (
        <Typography className={classes.emptyMessage}>
          Nenhum registro de histórico encontrado
        </Typography>
      ) : (
        <Box className={classes.tableContainer}>
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell style={{ width: "10%" }}>Data Contato</TableCell>
                <TableCell style={{ width: "10%" }}>Usuário</TableCell>
                <TableCell style={{ width: "10%" }}>Meio Contato</TableCell>
                <TableCell style={{ width: "20%" }}>Observações</TableCell>
                <TableCell style={{ width: "15%" }}>Resultado</TableCell>
                <TableCell style={{ width: "15%" }}>Próxima Ação</TableCell>
                <TableCell style={{ width: "10%" }}>Data Prox. Ação</TableCell>
                <TableCell style={{ width: "10%" }}>Contato</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {historico.map((item, index) => (
                <TableRow key={item.id || index}>
                  <TableCell>
                    {formatDateTime(item.data_visita || item.data_contato)}
                  </TableCell>
                  <TableCell>
                    {item.user?.name || "-"}
                  </TableCell>
                  <TableCell>
                    {getMotivoText(item.motivo_visita || item.meio_contato)}
                  </TableCell>
                  <TableCell>
                    {truncateText(item.descricao || item.observacoes, 120)}
                  </TableCell>
                  <TableCell>
                    {truncateText(item.resultado || item.resultado_visita, 80)}
                  </TableCell>
                  <TableCell>
                    {truncateText(item.proxima_acao, 80)}
                  </TableCell>
                  <TableCell>
                    {item.data_proxima_acao ? formatDateOnly(item.data_proxima_acao) : "-"}
                  </TableCell>
                  <TableCell>
                    {item.contato?.nome || "-"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* Rodapé */}
      <Box className={classes.footer}>
        <Typography>
          Total de registros: {historico?.length || 0}
        </Typography>
      </Box>
    </div>
  );
});

HistoricoPrintTemplate.displayName = "HistoricoPrintTemplate";

export default HistoricoPrintTemplate;
